/**
 * @file Mesure le texte visible d'un contenu HTML produit par l'éditeur riche.
 * @module common.utils.htmlText
 *
 * Source unique du comptage « texte visible » : le compteur de l'éditeur
 * (RHFEditor) et les validations qui bornent ce même texte doivent compter
 * pareil, sinon le marchand voit « 980/1000 » et se fait refuser sa saisie.
 */

/**
 * Extrait le texte visible d'un HTML : balises retirées, chaque fin de bloc
 * remplacée par `blockSeparator`, espaces insécables et suites d'espaces
 * ramenés à un espace, bords nettoyés.
 *
 * @param {string|null|undefined} html - Contenu HTML
 * @param {string} blockSeparator - Remplacement d'une fin de paragraphe, de titre, de puce ou d'un saut de ligne
 * @return {string} Texte visible, chaîne vide si absent
 */
const extractVisibleText = (html, blockSeparator) => {
  if (!html) return '';
  return html
      .replace(/<\/(?:p|h[1-6]|li)>|<br\s*\/?>/gi, blockSeparator)
      .replace(/<[^>]*>/g, '')
      .replace(/&nbsp;/g, ' ')
      // L'éditeur échappe « & », « < » et « > » : sans décodage, « Vins & Tapas »
      // se lirait « Vins &amp; Tapas ». `&amp;` en dernier, pour qu'un « &lt; »
      // tapé tel quel reste « &lt; ».
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, '\'')
      .replace(/&amp;/g, '&')
      .replace(/\s+/g, ' ')
      .trim();
};

/**
 * Extrait le texte visible d'un HTML, pour l'afficher ou le résumer. Une fin
 * de bloc devient un espace : sans lui, « Palacio !</p><p>Avec » se lirait
 * « Palacio !Avec ».
 *
 * @param {string|null|undefined} html - Contenu HTML
 * @return {string} Texte visible, chaîne vide si absent
 */
export const getHtmlVisibleText = (html) => extractVisibleText(html, ' ');

/**
 * Longueur du texte visible d'un HTML, telle qu'affichée par le compteur de
 * l'éditeur. Les fins de bloc ne comptent pas : un retour à la ligne n'est
 * pas un caractère saisi, et des conditions en puces proches de leur limite
 * deviendraient impossibles à enregistrer.
 *
 * @param {string|null|undefined} html - Contenu HTML
 * @return {number} Nombre de caractères visibles
 */
export const getHtmlVisibleLength = (html) => extractVisibleText(html, '').length;

/**
 * Indique si un HTML porte du texte visible. Un éditeur vidé laisse souvent
 * des balises seules (`<p><br></p>`), qui ne doivent pas compter comme du contenu.
 *
 * @param {string|null|undefined} html - Contenu HTML
 * @return {boolean} true s'il reste au moins un caractère visible
 */
export const hasHtmlVisibleText = (html) => getHtmlVisibleLength(html) > 0;
